/**
 * Presentation helpers shared by the desktop app and the web portal.
 *
 * Shared so that a size shown in one reads identically in the other. Two
 * independent implementations drift, and a user comparing the two screens
 * should never have to wonder which is telling the truth.
 */

import type { UnixSeconds } from "./types";

const UNITS = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"] as const;

/** Render a byte count the way a person reads it. */
export function humanBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";

  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit + 1 < UNITS.length) {
    value /= 1024;
    unit += 1;
  }
  return unit === 0 ? `${bytes} ${UNITS[0]}` : `${value.toFixed(1)} ${UNITS[unit]}`;
}

/** Render a Unix-seconds timestamp in the viewer's locale. */
export function humanTime(seconds: UnixSeconds): string {
  if (!seconds) return "—";
  return new Date(seconds * 1000).toLocaleString();
}

/** Render a duration given in whole seconds. */
export function humanDuration(seconds: number): string {
  if (seconds <= 0) return "under a second";
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * The proportion of logical data that deduplication removed.
 *
 * Returns `null` rather than zero when there is nothing to compare, so a brand
 * new repository does not display a confident "0% saved".
 */
export function deduplicationRatio(
  logicalBytes: number,
  storedBytes: number,
): number | null {
  if (logicalBytes <= 0) return null;
  return Math.max(0, Math.min(1, 1 - storedBytes / logicalBytes));
}

/** Render a 0–1 proportion as a whole-number percentage. */
export function percent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/**
 * Shorten a path from the left, keeping the end.
 *
 * The tail is the informative part: which file is involved matters more than
 * which drive it lives on.
 */
export function shortenPath(path: string, limit = 60): string {
  if (path.length <= limit) return path;
  return `…${path.slice(path.length - limit + 1)}`;
}

/** Shorten a long hex identifier for display. */
export function shortId(id: string, length = 12): string {
  return id.length <= length ? id : `${id.slice(0, length)}…`;
}

/** Join a list of names the way a sentence reads them: "A", "A and B", "A, B and C". */
export function joinWithAnd(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * Strip Windows' verbatim (`\\?\`) path prefix for display.
 *
 * `canonicalize` on Windows answers through this form even for an ordinary
 * local folder or a mapped drive — nobody spells or reads a path this way, so
 * every place the app shows a folder to a person runs it through this first.
 * Display only: nothing stored changes.
 */
const VERBATIM_UNC_PREFIX = "\\\\?\\UNC\\";
const VERBATIM_PREFIX = "\\\\?\\";

export function displayPath(path: string): string {
  if (path.startsWith(VERBATIM_UNC_PREFIX)) {
    return `\\\\${path.slice(VERBATIM_UNC_PREFIX.length)}`;
  }
  if (path.startsWith(VERBATIM_PREFIX)) return path.slice(VERBATIM_PREFIX.length);
  return path;
}

/**
 * The phrase a run's history row shows about the files it Missed, or `null`
 * when there is nothing to say.
 *
 * `null` for a run that Missed nothing, for every kind but a backup, and for
 * a run that never counted Missed files at all — the desktop's own runs
 * before `#370` load their count as zero (`PlanRunSummary` defaults it), and
 * the portal's `Snapshot.missedFiles` is absent rather than zero for the same
 * case. Both read as "nothing to say" here, deliberately: the row this
 * renders into looks the same either way — nothing a person needs to look
 * at — so the two apps do not need to tell them apart to agree on what the
 * row shows. (The absent-versus-zero distinction still matters upstream, in
 * `ingest.ts`'s `MissedCounts` and the sealed row it guards; it is a fact
 * about how sure the *count* is, not about how this phrase reads.)
 */
export function describeMissedBriefly(
  missedFiles: number | undefined,
  neverCapturedFiles: number | undefined,
): string | null {
  if (!missedFiles) return null;
  const missed = `${missedFiles.toLocaleString()} Missed`;
  if (!neverCapturedFiles) return missed;
  return `${missed}, ${neverCapturedFiles.toLocaleString()} never captured`;
}
